import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, X, ShieldAlert, Rocket, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { usePentest } from '../contexts/PentestContext';
import WizardProgress from '../components/compliance/WizardProgress';
import { TargetVerificationPanel } from './ApplicationDetailPage';

// Step definitions passed to the (now steps-configurable) WizardProgress.
const PENTEST_STEPS = [
  { number: 1, title: 'Target', description: 'Pick a target URL' },
  { number: 2, title: 'Verify', description: 'Confirm ownership' },
  { number: 3, title: 'Auth', description: 'Login configuration' },
  { number: 4, title: 'Scope', description: 'Scope & rate limit' },
  { number: 5, title: 'Attestation', description: 'Authorization' },
  { number: 6, title: 'Launch', description: 'Start the pentest' },
];

const splitList = (value) =>
  value
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);

const StartPentestWizard = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const {
    getApplication, loadApplications, verifiedMap, startPentest,
  } = usePentest();

  const [currentStep, setCurrentStep] = useState(1);
  const [launching, setLaunching] = useState(false);

  // Form state
  const [targetId, setTargetId] = useState('');
  const [loginUrl, setLoginUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [avoid, setAvoid] = useState('');
  const [focus, setFocus] = useState('');
  const [rateLimit, setRateLimit] = useState(10);
  const [attestation, setAttestation] = useState(false);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  // Derived during render so there is never a frame where loading has
  // finished but the application hasn't been picked out of the list yet.
  const application = getApplication(appId) || null;

  const targets = useMemo(
    () => (Array.isArray(application?.targets) ? application.targets : []),
    [application]
  );
  const selectedTarget = targets.find((t) => String(t.id) === String(targetId));
  const verified = selectedTarget
    ? (verifiedMap[selectedTarget.id] ?? !!selectedTarget.verified)
    : false;

  const rateLimitValue = Number(rateLimit);
  const rateLimitValid = Number.isFinite(rateLimitValue) && rateLimitValue >= 1;

  // The engine is white-box: the backend rejects a start without a linked repository.
  const hasRepo = !!application?.repoUrl;

  const canLaunch = attestation && verified && !!targetId && rateLimitValid && hasRepo;

  const handleCancel = () => {
    if (window.confirm('Cancel starting this pentest? Your selections will be lost.')) {
      navigate(`/applications/${appId}`);
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(`/applications/${appId}`);
    } else {
      setCurrentStep((s) => s - 1);
    }
  };

  const handleNext = () => {
    if (currentStep === 1 && !hasRepo) {
      toast.error('Link a repository on the application page before starting a pentest');
      return;
    }
    if (currentStep === 1 && !targetId) {
      toast.error('Please select a target');
      return;
    }
    if (currentStep === 2 && !verified) {
      toast.error('Verify ownership of the target before continuing');
      return;
    }
    if (currentStep === 4 && !rateLimitValid) {
      toast.error('Rate limit must be at least 1 request per second');
      return;
    }
    setCurrentStep((s) => Math.min(PENTEST_STEPS.length, s + 1));
  };

  const handleLaunch = async () => {
    if (!rateLimitValid) {
      toast.error('Rate limit must be at least 1 request per second');
      return;
    }
    if (!hasRepo) {
      toast.error('Link a repository on the application page before starting a pentest');
      return;
    }
    if (!canLaunch) {
      toast.error('You must verify the target and accept the authorization attestation');
      return;
    }
    setLaunching(true);
    // The test-account password has to reach the backend so the pentest agent
    // can log in. It is sent only over the HTTPS API call, never stored in
    // context, and cleared from component state as soon as the request returns.
    // The backend must not log request bodies for /pentest/start.
    const auth = loginUrl || username || password ? { loginUrl, username, password } : null;
    const pentestId = await startPentest({
      applicationId: appId,
      targetId,
      scope: { avoid: splitList(avoid), focus: splitList(focus) },
      rateLimit: rateLimitValue,
      auth,
      attestation: true,
    });
    setPassword('');
    setLaunching(false);
    if (pentestId) {
      navigate(`/pentest/${pentestId}/results`);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Select a target</h2>
            {/* Surfaced at step 1 rather than left to fail at launch: the engine is white-box
                and cannot run without source, so a missing repository blocks the whole wizard. */}
            {application && !application.repoUrl && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                <p className="font-semibold">This application has no linked repository.</p>
                <p className="mt-1">
                  Pentests analyze source code alongside the running app, so a target URL alone is
                  not enough. Add a repository on the{' '}
                  <Link
                    to={`/applications/${appId}`}
                    className="underline font-medium text-amber-900 hover:text-amber-950"
                  >
                    application page
                  </Link>{' '}
                  before starting.
                </p>
              </div>
            )}
            {targets.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
                This application has no targets yet. Add one from the application page first.
              </div>
            ) : (
              <div className="space-y-2">
                {targets.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTargetId(String(t.id))}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      String(targetId) === String(t.id)
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-mono text-sm text-gray-900 break-all">{t.targetUrl}</div>
                    <span className="text-xs text-gray-500 uppercase">{t.environment}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Verify ownership</h2>
            <p className="text-sm text-gray-500">
              You must prove you control the target before a pentest can run against it.
            </p>
            {selectedTarget ? (
              <div className="border border-gray-100 rounded-lg p-4">
                <div className="font-mono text-sm text-gray-900 break-all mb-3">{selectedTarget.targetUrl}</div>
                <TargetVerificationPanel target={selectedTarget} />
              </div>
            ) : (
              <p className="text-sm text-red-600">No target selected.</p>
            )}
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Authentication (optional)</h2>
            <p className="text-sm text-gray-500">
              Provide login details so the pentest can test authenticated areas. Leave blank for unauthenticated testing.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Login URL</label>
              <input
                type="url"
                value={loginUrl}
                onChange={(e) => setLoginUrl(e.target.value)}
                placeholder="https://staging.example.com/login"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="off"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Scope & rate limit</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Focus areas</label>
              <p className="text-xs text-gray-500 mb-1">Paths or features to prioritize (one per line or comma-separated).</p>
              <textarea
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
                rows={3}
                placeholder="/api, /admin, checkout flow"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Avoid</label>
              <p className="text-xs text-gray-500 mb-1">Paths or actions to steer clear of (one per line or comma-separated).</p>
              <textarea
                value={avoid}
                onChange={(e) => setAvoid(e.target.value)}
                rows={3}
                placeholder="/logout, /delete-account, payment submission"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rate limit (requests / second)</label>
              <input
                type="number"
                min="1"
                value={rateLimit}
                onChange={(e) => setRateLimit(e.target.value)}
                className="w-full sm:w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Authorization attestation</h2>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                Autonomous pentesting sends real attack traffic to the target. Only proceed against systems you are
                authorized to test.
              </p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={attestation}
                onChange={(e) => setAttestation(e.target.checked)}
                className="mt-1 h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">
                I confirm I am authorized to perform security testing against{' '}
                <span className="font-mono">{selectedTarget?.targetUrl || 'this target'}</span>, and I accept
                responsibility for this activity.
              </span>
            </label>
            {!verified && (
              <p className="text-sm text-red-600">Target ownership is not verified — go back to the Verify step.</p>
            )}
          </div>
        );
      case 6:
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Ready to launch</h2>
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-4 space-y-2 text-sm text-gray-700">
              <div><span className="text-gray-500">Target:</span> <span className="font-mono break-all">{selectedTarget?.targetUrl}</span></div>
              <div><span className="text-gray-500">Environment:</span> {selectedTarget?.environment}</div>
              <div><span className="text-gray-500">Authenticated:</span> {loginUrl || username ? 'Yes' : 'No'}</div>
              <div><span className="text-gray-500">Rate limit:</span> {rateLimit} req/s</div>
              <div><span className="text-gray-500">Focus:</span> {splitList(focus).join(', ') || 'default'}</div>
              <div><span className="text-gray-500">Avoid:</span> {splitList(avoid).join(', ') || 'none'}</div>
              <div><span className="text-gray-500">Ownership verified:</span> {verified ? 'Yes' : 'No'}</div>
              <div><span className="text-gray-500">Attestation accepted:</span> {attestation ? 'Yes' : 'No'}</div>
            </div>
            <button
              onClick={handleLaunch}
              disabled={!canLaunch || launching}
              className={`w-full flex justify-center items-center gap-2 px-6 py-3 min-h-[48px] rounded-lg font-semibold text-white transition-colors ${
                canLaunch && !launching ? 'bg-teal-600 hover:bg-teal-700' : 'bg-teal-600 opacity-50 cursor-not-allowed'
              }`}
            >
              {launching ? <Loader className="w-5 h-5 animate-spin" /> : <Rocket className="w-5 h-5" />}
              Launch Pentest
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-14 md:top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Go back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Start Pentest</h1>
                <p className="text-sm text-gray-500">{application?.name || 'Application'}</p>
              </div>
            </div>
            <button
              onClick={handleCancel}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Cancel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Progress Bar (reuses WizardProgress with a custom step set) */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <WizardProgress currentStep={currentStep} steps={PENTEST_STEPS} />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {renderStep()}

        {/* Navigation (launch lives inside step 6) */}
        {currentStep < PENTEST_STEPS.length && (
          <div className="flex flex-col-reverse gap-3 pt-6 mt-6 border-t border-gray-200 sm:flex-row sm:justify-between">
            <button
              onClick={handleBack}
              className="px-4 py-2 min-h-[44px] text-sm text-gray-500 hover:text-gray-700 font-medium"
            >
              Back
            </button>
            <button
              onClick={handleNext}
              className="flex w-full sm:w-auto justify-center items-center gap-2 px-6 py-3 min-h-[44px] bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
            >
              Continue
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartPentestWizard;
